// src/types/common.ts
function isCuid(str) {
  return /^c[a-z0-9]{24}$/.test(str);
}

// src/constants/models.ts
var DEFAULT_SONNET_MODEL = "claude-sonnet-5";
var DEFAULT_OPUS_MODEL = "claude-opus-4-8";
var DEFAULT_HAIKU_MODEL = "claude-haiku-4-5-20251001";
var PREVIOUS_SONNET_MODEL = "claude-sonnet-4-6";
var RETIRED_SONNET_MODEL = "claude-sonnet-4-20250514";

// src/types/project-settings-types.ts
var PREVIEW_PORT_DENY_LIST = [5432, 6379, 9200];
function isAllowablePreviewPort(port) {
  if (typeof port !== "number" || !Number.isInteger(port)) return false;
  if (port < 1 || port > 65535) return false;
  return !PREVIEW_PORT_DENY_LIST.includes(port);
}
function sanitizeSessionPreviewPorts(ports) {
  if (!Array.isArray(ports)) return [];
  const seen = /* @__PURE__ */ new Set();
  const out = [];
  for (const raw of ports) {
    if (!raw || typeof raw !== "object") continue;
    const portValue = raw.port;
    if (!isAllowablePreviewPort(portValue)) continue;
    if (seen.has(portValue)) continue;
    seen.add(portValue);
    const labelValue = raw.label;
    const visValue = raw.visibility;
    out.push({
      port: portValue,
      ...typeof labelValue === "string" ? { label: labelValue } : {},
      ...visValue === "public" || visValue === "private" ? { visibility: visValue } : {}
    });
  }
  return out;
}
function resolveAllowedPreviewPorts(sources) {
  const set = /* @__PURE__ */ new Set();
  const add = (p) => {
    if (isAllowablePreviewPort(p)) set.add(p);
  };
  add(sources.taskPreviewPort);
  add(sources.projectPreviewPort);
  if (Array.isArray(sources.sessionPreviewPorts)) {
    for (const entry of sources.sessionPreviewPorts) add(entry?.port);
  }
  return [...set].sort((a, b) => a - b);
}
function pickPreferredPreviewPort(sources) {
  const fallback = sources.defaultPort ?? 3050;
  if (isAllowablePreviewPort(sources.taskPreviewPort)) return sources.taskPreviewPort;
  const sessionPorts = sanitizeSessionPreviewPorts(sources.sessionPreviewPorts);
  const firstSession = sessionPorts[0]?.port;
  if (isAllowablePreviewPort(firstSession)) return firstSession;
  if (isAllowablePreviewPort(sources.projectPreviewPort)) return sources.projectPreviewPort;
  return fallback;
}
var PROJECT_FUNCTION_DEFAULTS = {
  identification: {
    model: DEFAULT_SONNET_MODEL,
    effort: "low",
    thinking: { type: "adaptive" }
  },
  suggestionHandler: {
    model: DEFAULT_SONNET_MODEL,
    effort: "high",
    thinking: { type: "adaptive" }
  },
  prReview: {
    model: DEFAULT_SONNET_MODEL,
    effort: "low",
    thinking: { type: "adaptive" }
  }
};
function resolveProjectFunctionConfig(settings, functionId) {
  const defaults = PROJECT_FUNCTION_DEFAULTS[functionId];
  const override = settings?.functionSettings?.[functionId];
  return {
    model: override?.model ?? defaults.model,
    effort: override?.effort ?? defaults.effort,
    thinking: override?.thinking ?? defaults.thinking,
    instructions: override?.instructions
  };
}
function resolveCodeReviewMode(settings) {
  const s = settings ?? {};
  if (s.codeReviewMode) return s.codeReviewMode;
  if (s.codeReviewEnabled === true) return "blocking";
  return "off";
}
function resolveGcpEnvResources(settings) {
  const s = settings ?? {};
  if (s.gcpEnvResources) return s.gcpEnvResources;
  if (s.gcpCloudRunServices?.length || s.gcpCloudSqlInstances?.length) {
    return {
      prod: {
        cloudRunServices: s.gcpCloudRunServices,
        cloudSqlInstances: s.gcpCloudSqlInstances
      }
    };
  }
  return {};
}

// src/types/gcp-types.ts
var GCP_ENVS = ["prod", "dev", "claudespace"];

// src/types/agent-project-types.ts
var TASK_STATUS_ORDER = [
  "Planning",
  "Open",
  "InProgress",
  "ReviewPR",
  "ReviewDev",
  "ReviewLive",
  "Complete",
  "Cancelled"
];

// src/types/task-types.ts
var CARD_TYPE_VALUES = ["task", "incident", "suggestion", "ephemeral"];
var UNIFIED_STATUSES = [
  "Planning",
  "Open",
  "InProgress",
  "ReviewPR",
  "ReviewDev",
  "ReviewLive",
  "Complete",
  "Cancelled"
];
var CARD_TYPE_STATUSES = {
  task: UNIFIED_STATUSES,
  incident: UNIFIED_STATUSES,
  suggestion: UNIFIED_STATUSES,
  // Ephemeral cards (e.g. automated code review) are created past Planning and
  // only ever live in InProgress → Complete/Cancelled, but reuse the unified set
  // so status validation never rejects a shared transition.
  ephemeral: UNIFIED_STATUSES
};
function validateStatusForType(type, status) {
  return CARD_TYPE_STATUSES[type].includes(status);
}

// src/types/service-map.ts
var ALLOWED_FILE_MIME_TYPES = [
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
  "application/json",
  "text/csv",
  "text/plain",
  "text/markdown",
  "application/pdf"
];
var MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024;
var EMBED_THRESHOLD_IMAGES = 5 * 1024 * 1024;
var EMBED_THRESHOLD_TEXT = 2 * 1024 * 1024;
var EMBEDDABLE_IMAGE_TYPES = [
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp"
];
var EMBEDDABLE_TEXT_TYPES = [
  "text/plain",
  "text/csv",
  "text/markdown",
  "application/json"
];
function isEmbeddableFile(mimeType, fileSize) {
  if (EMBEDDABLE_IMAGE_TYPES.some((t) => mimeType === t)) {
    return fileSize <= EMBED_THRESHOLD_IMAGES;
  }
  if (EMBEDDABLE_TEXT_TYPES.some((t) => mimeType === t)) {
    return fileSize <= EMBED_THRESHOLD_TEXT;
  }
  return false;
}

// src/types/connection-state.ts
var IDLE_HEARTBEAT_MS = 90 * 1e3;
var STRANDED_RUNNER_STATUSES = /* @__PURE__ */ new Set(["stalled_heartbeat", "stranded", "crashed"]);
function deriveConnectionState(inputs, now = Date.now()) {
  const heartbeatAgeMs = inputs.lastHeartbeatAt ? Math.max(0, now - new Date(inputs.lastHeartbeatAt).getTime()) : null;
  if (inputs.deletionRequestedAt) {
    return { state: "pendingDeletion", heartbeatAgeMs };
  }
  const status = inputs.codespaceStatus;
  if (status === "Deleted") {
    return { state: "deleted", heartbeatAgeMs };
  }
  if (status === "Creating" || inputs.podStatus === "Creating") {
    return { state: "creating", heartbeatAgeMs };
  }
  if (inputs.agentRunnerStatus && STRANDED_RUNNER_STATUSES.has(inputs.agentRunnerStatus)) {
    return { state: "stranded", heartbeatAgeMs };
  }
  if (status === "Stopped" || inputs.podStatus === "Stopped" || inputs.podStatus === "Sleeping") {
    return { state: "stopped", heartbeatAgeMs };
  }
  if (status !== "Running" && !inputs.codespaceId && !inputs.podName) {
    return { state: "none", heartbeatAgeMs };
  }
  if (heartbeatAgeMs !== null && heartbeatAgeMs >= IDLE_HEARTBEAT_MS) {
    return { state: "idle", heartbeatAgeMs };
  }
  if (status === "Running") {
    return { state: "active", heartbeatAgeMs };
  }
  return { state: "none", heartbeatAgeMs };
}
function formatHeartbeatAge(ms) {
  if (ms === null) return "never";
  const sec = Math.floor(ms / 1e3);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.floor(hr / 24);
  return `${days}d ago`;
}

// src/types/agent-session-requests.ts
import { z } from "zod";
var AgentHeartbeatSchema = z.object({
  sessionId: z.string().optional(),
  timestamp: z.string(),
  status: z.enum(["active", "idle", "building"]),
  currentAction: z.string().optional()
});
var CreatePRInputSchema = z.object({
  title: z.string().min(1),
  body: z.string(),
  head: z.string().optional(),
  base: z.string().optional()
});
var PostToChatInputSchema = z.object({
  message: z.string().min(1),
  type: z.enum(["message", "question", "update"]).optional().default("message")
});
var GetTaskContextRequestSchema = z.object({
  sessionId: z.string(),
  includeHistory: z.boolean().optional().default(false)
});
var GetChatMessagesRequestSchema = z.object({
  sessionId: z.string(),
  limit: z.number().int().positive().optional().default(50),
  offset: z.number().int().nonnegative().optional().default(0)
});
var GetTaskFilesRequestSchema = z.object({
  sessionId: z.string()
});
var GetTaskFileRequestSchema = z.object({
  sessionId: z.string(),
  fileId: z.string()
});
var GetTaskRequestSchema = z.object({
  sessionId: z.string(),
  taskSlugOrId: z.string()
});
var GetCliHistoryRequestSchema = z.object({
  sessionId: z.string(),
  limit: z.number().int().positive().optional().default(100),
  source: z.enum(["agent", "application"]).optional()
});
var ListSubtasksRequestSchema = z.object({
  sessionId: z.string()
});
var GetDependenciesRequestSchema = z.object({
  sessionId: z.string()
});
var GetSuggestionsRequestSchema = z.object({
  sessionId: z.string(),
  status: z.string().optional(),
  limit: z.number().int().min(1).max(100).optional()
});
var ListManualTestsRequestSchema = z.object({
  sessionId: z.string()
});
var QueryManualTestsRequestSchema = z.object({
  sessionId: z.string(),
  cardStatuses: z.array(z.string()).optional(),
  testStatuses: z.array(z.enum(["open", "approved", "rejected"])).optional()
});
var PostToChatRequestSchema = PostToChatInputSchema;
var CreatePullRequestRequestSchema = CreatePRInputSchema.extend({ sessionId: z.string() });
var RequestFileUploadRequestSchema = z.object({
  sessionId: z.string(),
  fileName: z.string().min(1).max(255),
  mimeType: z.string().min(1).max(128),
  fileSize: z.number().int().positive().max(MAX_FILE_SIZE_BYTES)
});
var ConfirmFileUploadRequestSchema = z.object({
  sessionId: z.string(),
  fileId: z.string(),
  title: z.string().max(500).optional()
});
var UpdateTaskStatusRequestSchema = z.object({
  sessionId: z.string(),
  status: z.string(),
  force: z.boolean().optional().default(false)
});
var StoreSessionIdRequestSchema = z.object({
  sessionId: z.string(),
  sdkSessionId: z.string()
});
var SetManualTestsRequestSchema = z.object({
  sessionId: z.string(),
  items: z.array(z.object({ title: z.string().min(1) })).min(1)
});
var EditManualTestRequestSchema = z.object({
  sessionId: z.string(),
  title: z.string().min(1),
  newTitle: z.string().min(1)
});
var RemoveManualTestRequestSchema = z.object({
  sessionId: z.string(),
  title: z.string().min(1)
});
var ApproveManualTestRequestSchema = z.object({
  sessionId: z.string(),
  title: z.string().min(1)
});
var RejectManualTestRequestSchema = z.object({
  sessionId: z.string(),
  title: z.string().min(1),
  reason: z.string().min(1).max(2e3)
});
var TrackSpendingRequestSchema = z.object({
  sessionId: z.string(),
  inputTokens: z.number().int().nonnegative(),
  outputTokens: z.number().int().nonnegative(),
  costUsd: z.number().nonnegative(),
  model: z.string()
});
var HeartbeatRequestSchema = AgentHeartbeatSchema;
var SessionStartRequestSchema = z.object({
  sessionId: z.string(),
  agentVersion: z.string(),
  capabilities: z.array(z.string())
});
var SessionStopRequestSchema = z.object({
  sessionId: z.string(),
  reason: z.string().optional()
});
var ConnectAgentRequestSchema = z.object({
  sessionId: z.string()
});
var ReportAgentStatusRequestSchema = z.object({
  sessionId: z.string(),
  status: z.string()
});
var NotifyAgentVersionRequestSchema = z.object({
  sessionId: z.string(),
  agentVersion: z.string()
});
var CreateSubtaskRequestSchema = z.object({
  sessionId: z.string(),
  title: z.string().min(1),
  description: z.string().optional(),
  plan: z.string().optional(),
  storyPointValue: z.number().int().positive().optional(),
  ordinal: z.number().int().nonnegative().optional()
});
var UpdateSubtaskRequestSchema = z.object({
  sessionId: z.string(),
  subtaskId: z.string(),
  title: z.string().min(1).optional(),
  description: z.string().optional(),
  plan: z.string().optional(),
  status: z.string().optional(),
  storyPointValue: z.number().int().positive().optional()
});
var DeleteSubtaskRequestSchema = z.object({
  sessionId: z.string(),
  subtaskId: z.string()
});
var GetTaskPropertiesRequestSchema = z.object({
  sessionId: z.string()
});
var GetCumulativeSpendingRequestSchema = z.object({
  sessionId: z.string()
});
var ModelUsageEntrySchema = z.object({
  model: z.string(),
  inputTokens: z.number().nonnegative(),
  outputTokens: z.number().nonnegative(),
  cacheReadInputTokens: z.number().nonnegative(),
  cacheCreationInputTokens: z.number().nonnegative(),
  costUSD: z.number().nonnegative()
});
var GetCumulativeSpendingResponseSchema = z.object({
  totalCostUsd: z.number().nonnegative(),
  modelUsage: z.array(ModelUsageEntrySchema)
});
var UpdateTaskFieldsRequestSchema = z.object({
  sessionId: z.string(),
  plan: z.string().optional(),
  description: z.string().optional()
});
var UpdateTaskPropertiesRequestSchema = z.object({
  sessionId: z.string(),
  title: z.string().optional(),
  storyPointValue: z.number().int().positive().optional(),
  tagIds: z.array(z.string()).optional(),
  tagNames: z.array(z.string()).optional(),
  githubPRUrl: z.string().url().optional(),
  githubBranch: z.string().optional()
});
var ListIconsRequestSchema = z.object({
  sessionId: z.string()
});
var GenerateTaskIconRequestSchema = z.object({
  sessionId: z.string(),
  prompt: z.string().min(1),
  aspectRatio: z.string().optional()
});
var SearchFaIconsRequestSchema = z.object({
  sessionId: z.string(),
  query: z.string().min(1),
  first: z.number().int().positive().optional()
});
var PickFaIconRequestSchema = z.object({
  sessionId: z.string(),
  fontAwesomeId: z.string().min(1),
  fontAwesomeStyle: z.string().optional()
});
var CreateFollowUpTaskRequestSchema = z.object({
  sessionId: z.string(),
  title: z.string().min(1),
  description: z.string().optional(),
  plan: z.string().optional(),
  storyPointValue: z.number().int().positive().optional()
});
var AddDependencyRequestSchema = z.object({
  sessionId: z.string(),
  dependsOnSlugOrId: z.string()
});
var RemoveDependencyRequestSchema = z.object({
  sessionId: z.string(),
  dependsOnSlugOrId: z.string()
});
var CreateSuggestionRequestSchema = z.object({
  sessionId: z.string(),
  title: z.string().min(1),
  description: z.string().optional(),
  tagNames: z.array(z.string()).optional()
});
var VoteSuggestionRequestSchema = z.object({
  sessionId: z.string(),
  suggestionId: z.string(),
  value: z.union([z.literal(1), z.literal(-1)])
});
var TriggerIdentificationRequestSchema = z.object({
  sessionId: z.string()
});
var SubmitCodeReviewResultRequestSchema = z.object({
  sessionId: z.string(),
  approved: z.boolean(),
  content: z.string()
});
var StartChildCloudBuildRequestSchema = z.object({
  sessionId: z.string(),
  childTaskId: z.string()
});
var StopChildBuildRequestSchema = z.object({
  sessionId: z.string(),
  childTaskId: z.string()
});
var ApproveAndMergePRRequestSchema = z.object({
  sessionId: z.string(),
  childTaskId: z.string()
});
var PostChildChatMessageRequestSchema = z.object({
  sessionId: z.string(),
  childTaskId: z.string(),
  message: z.string().min(1)
});
var UpdateChildStatusRequestSchema = z.object({
  sessionId: z.string(),
  childTaskId: z.string(),
  status: z.string()
});
var GetAgentStatusRequestSchema = z.object({
  taskId: z.string()
});
var GetUiCliHistoryRequestSchema = z.object({
  taskId: z.string()
});
var GetActivePtySessionRequestSchema = z.object({
  taskId: z.string()
});
var SendSoftStopRequestSchema = z.object({
  taskId: z.string()
});
var FlushTaskQueueRequestSchema = z.object({
  taskId: z.string(),
  softStop: z.boolean().optional()
});
var CancelTaskQueuedMessageRequestSchema = z.object({
  taskId: z.string(),
  messageId: z.string()
});
var FlushSingleQueuedMessageRequestSchema = z.object({
  taskId: z.string(),
  messageId: z.string(),
  softStop: z.boolean().optional()
});
var AnswerAgentQuestionRequestSchema = z.object({
  taskId: z.string(),
  requestId: z.string(),
  answers: z.record(z.string(), z.string())
});
var ClearAgentTodosRequestSchema = z.object({
  taskId: z.string()
});
var AgentQuestionOptionSchema = z.object({
  label: z.string(),
  description: z.string(),
  preview: z.string().optional()
});
var AgentQuestionSchema = z.object({
  question: z.string(),
  header: z.string(),
  options: z.array(AgentQuestionOptionSchema),
  multiSelect: z.boolean().optional()
});
var AskUserQuestionRequestSchema = z.object({
  sessionId: z.string(),
  question: z.string().min(1),
  requestId: z.string().min(1),
  questions: z.array(AgentQuestionSchema).min(1)
});
var AgentEventSchema = z.object({
  type: z.string().min(1)
}).catchall(z.unknown());
var EmitAgentEventRequestSchema = z.object({
  sessionId: z.string(),
  events: z.array(AgentEventSchema).max(500)
});
var RefreshGithubTokenRequestSchema = z.object({
  sessionId: z.string()
});
var RefreshGithubTokenResponseSchema = z.object({
  token: z.string()
});
var PTY_FRAME_MAX_CHARS = 256 * 1024;
var PTY_MAX_DIMENSION = 1e3;
var PtyOutputRequestSchema = z.object({
  sessionId: z.string(),
  data: z.string().max(PTY_FRAME_MAX_CHARS),
  cols: z.number().int().positive().max(PTY_MAX_DIMENSION).optional(),
  rows: z.number().int().positive().max(PTY_MAX_DIMENSION).optional()
});
var PtyInputRequestSchema = z.object({
  sessionId: z.string(),
  data: z.string().max(PTY_FRAME_MAX_CHARS)
});
var PtyResizeRequestSchema = z.object({
  sessionId: z.string(),
  cols: z.number().int().positive().max(PTY_MAX_DIMENSION),
  rows: z.number().int().positive().max(PTY_MAX_DIMENSION)
});
var PtyAttachRequestSchema = z.object({
  sessionId: z.string()
});
var CreatePRResponseSchema = z.object({
  prNumber: z.number().int().positive(),
  prUrl: z.string().url()
});
var PostToChatResponseSchema = z.object({
  messageId: z.string()
});
var UpdateTaskStatusResponseSchema = z.object({
  taskId: z.string(),
  status: z.string()
});
var StoreSessionIdResponseSchema = z.object({
  success: z.boolean()
});
var HeartbeatResponseSchema = z.object({
  acknowledged: z.boolean()
});
var SessionStartResponseSchema = z.object({
  sessionId: z.string(),
  startedAt: z.string()
});
var SessionStopResponseSchema = z.object({
  sessionId: z.string(),
  stoppedAt: z.string()
});
var DeleteSubtaskResponseSchema = z.object({
  deleted: z.boolean()
});

// src/types/agent-session-project-requests.ts
import { z as z2 } from "zod";
var ListAccessibleProjectsRequestSchema = z2.object({
  pageSize: z2.number().int().positive().max(100).optional().default(100)
});
var ListProjectTasksRequestSchema = z2.object({
  projectId: z2.string(),
  status: z2.string().optional(),
  assigneeId: z2.string().optional(),
  unassigned: z2.boolean().optional(),
  limit: z2.number().int().positive().optional().default(50)
}).refine((p) => !(p.unassigned && p.assigneeId), {
  message: "Pass either assigneeId or unassigned, not both"
});
var GetProjectTaskRequestSchema = z2.object({
  projectId: z2.string(),
  taskId: z2.string()
});
var SearchProjectTasksRequestSchema = z2.object({
  projectId: z2.string(),
  tagNames: z2.array(z2.string()).optional(),
  searchQuery: z2.string().optional(),
  statusFilters: z2.array(z2.string()).optional(),
  // Card types to include. Omitted/empty → defaults to ["task"] in the handler so
  // search doesn't surface incidents/suggestions unless asked. Enum validation lives
  // at the MCP tool layer (mirrors statusFilters).
  typeFilters: z2.array(z2.string()).optional(),
  assigneeId: z2.string().optional(),
  unassigned: z2.boolean().optional(),
  limit: z2.number().int().positive().optional().default(20)
}).refine((p) => !(p.unassigned && p.assigneeId), {
  message: "Pass either assigneeId or unassigned, not both"
});
var ListProjectTagsRequestSchema = z2.object({
  projectId: z2.string()
});
var GetProjectSummaryRequestSchema = z2.object({
  projectId: z2.string()
});
var CreateProjectTaskRequestSchema = z2.object({
  projectId: z2.string(),
  title: z2.string().min(1),
  description: z2.string().optional(),
  plan: z2.string().optional(),
  status: z2.string().optional(),
  requestingUserId: z2.string().optional()
});
var UpdateProjectTaskRequestSchema = z2.object({
  projectId: z2.string(),
  taskId: z2.string(),
  title: z2.string().optional(),
  description: z2.string().optional(),
  plan: z2.string().optional(),
  status: z2.string().optional(),
  assignedUserId: z2.string().nullish(),
  requestingUserId: z2.string().optional()
});
var PostToProjectTaskChatRequestSchema = z2.object({
  projectId: z2.string(),
  taskId: z2.string(),
  content: z2.string(),
  requestingUserId: z2.string().optional()
});
var GetProjectTaskCliRequestSchema = z2.object({
  projectId: z2.string(),
  taskId: z2.string(),
  limit: z2.number().int().positive().optional().default(50),
  source: z2.string().optional()
});
var GetProjectTaskSessionsRequestSchema = z2.object({
  projectId: z2.string(),
  taskId: z2.string()
});
var StartProjectBuildRequestSchema = z2.object({
  projectId: z2.string(),
  taskId: z2.string(),
  requestingUserId: z2.string().optional()
});
var StopProjectBuildRequestSchema = z2.object({
  projectId: z2.string(),
  taskId: z2.string(),
  requestingUserId: z2.string().optional()
});
var CreateProjectReleaseRequestSchema = z2.object({
  projectId: z2.string(),
  taskIds: z2.array(z2.string()).optional(),
  requestingUserId: z2.string().optional()
});
var ApproveProjectMergePRRequestSchema = z2.object({
  projectId: z2.string(),
  childTaskId: z2.string(),
  requestingUserId: z2.string().optional()
});
var ListProjectSubtasksRequestSchema = z2.object({
  projectId: z2.string(),
  taskId: z2.string()
});
var CreateProjectSubtaskRequestSchema = z2.object({
  projectId: z2.string(),
  parentTaskId: z2.string(),
  title: z2.string().min(1),
  description: z2.string().optional(),
  plan: z2.string().optional(),
  ordinal: z2.number().int().nonnegative().optional(),
  storyPointValue: z2.number().int().positive().optional(),
  requestingUserId: z2.string().optional()
});
var UpdateProjectSubtaskRequestSchema = z2.object({
  projectId: z2.string(),
  subtaskId: z2.string(),
  title: z2.string().optional(),
  description: z2.string().optional(),
  plan: z2.string().optional(),
  status: z2.string().optional(),
  ordinal: z2.number().int().nonnegative().optional(),
  storyPointValue: z2.number().int().positive().optional(),
  requestingUserId: z2.string().optional()
});
var DeleteProjectSubtaskRequestSchema = z2.object({
  projectId: z2.string(),
  subtaskId: z2.string(),
  requestingUserId: z2.string().optional()
});
var GetProjectTaskChatRequestSchema = z2.object({
  projectId: z2.string(),
  taskId: z2.string(),
  limit: z2.number().int().positive().optional().default(20)
});
var AddProjectTaskDependencyRequestSchema = z2.object({
  projectId: z2.string(),
  taskId: z2.string(),
  dependsOnSlugOrId: z2.string(),
  requestingUserId: z2.string().optional()
});
var RemoveProjectTaskDependencyRequestSchema = z2.object({
  projectId: z2.string(),
  taskId: z2.string(),
  dependsOnSlugOrId: z2.string(),
  requestingUserId: z2.string().optional()
});
var VoteProjectSuggestionRequestSchema = z2.object({
  projectId: z2.string(),
  suggestionId: z2.string(),
  value: z2.union([z2.literal(1), z2.literal(-1)]),
  requestingUserId: z2.string().optional()
});
var GetProjectTaskDependenciesRequestSchema = z2.object({
  projectId: z2.string(),
  taskId: z2.string()
});
var ListProjectTaskFilesRequestSchema = z2.object({
  projectId: z2.string(),
  taskId: z2.string()
});
var GetProjectAttachmentRequestSchema = z2.object({
  projectId: z2.string(),
  taskId: z2.string(),
  fileId: z2.string(),
  /** Byte offset into text content (paging large logs/JSON). Default 0. */
  offset: z2.number().int().nonnegative().optional(),
  /** Max bytes of text content to return from `offset`. Server default applies. */
  maxBytes: z2.number().int().positive().optional()
});
var RequestProjectFileUploadRequestSchema = z2.object({
  projectId: z2.string(),
  taskId: z2.string(),
  fileName: z2.string().min(1).max(255),
  mimeType: z2.string().min(1).max(128),
  fileSize: z2.number().int().positive().max(MAX_FILE_SIZE_BYTES),
  requestingUserId: z2.string().optional()
});
var ConfirmProjectFileUploadRequestSchema = z2.object({
  projectId: z2.string(),
  taskId: z2.string(),
  fileId: z2.string(),
  /** When set, the attachment is also posted to the task chat with this text. */
  comment: z2.string().max(2e3).optional(),
  requestingUserId: z2.string().optional()
});
var CreateProjectPullRequestRequestSchema = z2.object({
  projectId: z2.string(),
  taskId: z2.string(),
  title: z2.string().min(1),
  body: z2.string(),
  head: z2.string().optional(),
  base: z2.string().optional(),
  requestingUserId: z2.string().optional()
});
var ListProjectMembersRequestSchema = z2.object({
  projectId: z2.string()
});
var AddProjectTaskReviewerRequestSchema = z2.object({
  projectId: z2.string(),
  taskId: z2.string(),
  userId: z2.string(),
  requestingUserId: z2.string().optional()
});
var RemoveProjectTaskReviewerRequestSchema = z2.object({
  projectId: z2.string(),
  taskId: z2.string(),
  userId: z2.string(),
  requestingUserId: z2.string().optional()
});
var ListProjectManualTestsRequestSchema = z2.object({
  projectId: z2.string(),
  taskId: z2.string()
});
var QueryProjectManualTestsRequestSchema = z2.object({
  projectId: z2.string(),
  cardStatuses: z2.array(z2.string()).optional(),
  testStatuses: z2.array(z2.enum(["open", "approved", "rejected"])).optional()
});
var SetProjectManualTestsRequestSchema = z2.object({
  projectId: z2.string(),
  taskId: z2.string(),
  items: z2.array(z2.object({ title: z2.string().min(1) })).min(1),
  requestingUserId: z2.string().optional()
});
var EditProjectManualTestRequestSchema = z2.object({
  projectId: z2.string(),
  taskId: z2.string(),
  title: z2.string().min(1),
  newTitle: z2.string().min(1),
  requestingUserId: z2.string().optional()
});
var RemoveProjectManualTestRequestSchema = z2.object({
  projectId: z2.string(),
  taskId: z2.string(),
  title: z2.string().min(1),
  requestingUserId: z2.string().optional()
});
var ApproveProjectManualTestRequestSchema = z2.object({
  projectId: z2.string(),
  taskId: z2.string(),
  title: z2.string().min(1),
  requestingUserId: z2.string().optional()
});
var RejectProjectManualTestRequestSchema = z2.object({
  projectId: z2.string(),
  taskId: z2.string(),
  title: z2.string().min(1),
  reason: z2.string().min(1).max(2e3),
  requestingUserId: z2.string().optional()
});
var CreateProjectSuggestionRequestSchema = z2.object({
  projectId: z2.string(),
  title: z2.string().min(1),
  description: z2.string().optional(),
  tagNames: z2.array(z2.string()).optional(),
  requestingUserId: z2.string().optional()
});

// src/types/agent-session-events.ts
var CRITICAL_AUTOMATED_SOURCES = /* @__PURE__ */ new Set([
  "ci_failure",
  "review_trigger",
  "merge_conflict",
  "merge_failed",
  "pull_branch"
]);
var createAgentSessionRoom = (sessionId) => `agentSession:${sessionId}`;
var createProjectRoom = (projectId) => `project:${projectId}`;

// src/types/deployment-service-types.ts
var DEPLOYMENT_TARGET_PROVIDERS = [
  "pm2-local",
  "github-actions",
  "vercel",
  "cloud-run",
  "gke"
];
var DEPLOYMENT_TARGET_ENVIRONMENTS = ["dev", "prod", "staging", "custom"];

// src/generated/mcp-tool-registry.generated.ts
var MCP_TOOL_REGISTRY = [
  {
    name: "upload_attachment",
    description: "Upload an image file (e.g. a Playwright screenshot) as a task attachment AND post it to the task chat in one step \u2014 no follow-up post_to_chat call needed. Supports png/jpg/gif/webp.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/attachment-tools.ts",
    category: "global",
    modes: "all PM/agent modes (common tools)"
  },
  {
    name: "approve_manual_test",
    description: "Sign off on (approve) a manual test step on behalf of your authenticated user. Identify the test by its title (case-insensitive). Use after you have verified the step passes.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/checklist-tools.ts",
    category: "global",
    modes: "all PM/agent modes (common tools)"
  },
  {
    name: "edit_manual_test",
    description: "Rename an existing manual test step. Identify the test by its current title (case-insensitive); pass the new title to replace it. Use to correct or refine a recorded manual verification step.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/checklist-tools.ts",
    category: "global",
    modes: "all PM/agent modes (common tools)"
  },
  {
    name: "list_manual_tests",
    description: "List the manual test checklist items for the current task. Use to see what manual verification steps have already been recorded.",
    readOnly: true,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/checklist-tools.ts",
    category: "global",
    modes: "all PM/agent modes (common tools)"
  },
  {
    name: "query_manual_tests",
    description: "Query manual tests across many tasks in this project, grouped by task. Filter by card status (ReviewDev, ReviewLive, Complete, ...) and/or test status (open | approved | rejected). Use to answer 'show all OPEN manual tests in ReviewDev' or 'show all REJECTED manual tests with the failing reason'. With no filters it defaults to the needs-attention view: open+rejected tests on ReviewDev/ReviewLive cards.",
    readOnly: true,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/checklist-tools.ts",
    category: "global",
    modes: "all PM/agent modes (common tools)"
  },
  {
    name: "reject_manual_test",
    description: "Flag an issue with (reject) a manual test step on behalf of your authenticated user, recording the reason. Identify the test by its title (case-insensitive). Use when the step fails verification.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/checklist-tools.ts",
    category: "global",
    modes: "all PM/agent modes (common tools)"
  },
  {
    name: "remove_manual_test",
    description: "Remove an existing manual test step from the task checklist. Identify the test by its title (case-insensitive). Use to delete a stale or incorrect manual verification step.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/checklist-tools.ts",
    category: "global",
    modes: "all PM/agent modes (common tools)"
  },
  {
    name: "set_manual_tests",
    description: "Add manual test steps to the task checklist. Existing items with the same title are automatically skipped (deduplication). Use to record specific manual verification steps that reviewers should follow when testing this PR.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/checklist-tools.ts",
    category: "global",
    modes: "all PM/agent modes (common tools)"
  },
  {
    name: "approve_code_review",
    description: "Approve the code review and exit. Use when the diff passes all review criteria. Takes only a summary \u2014 for changes, use request_code_changes with a structured issues[] list.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/code-review-tools.ts",
    category: "codeReview",
    modes: "review"
  },
  {
    name: "request_code_changes",
    description: "Request changes during code review and exit. Use when substantive issues must be fixed before merge. Each issue: { file, line?, severity: critical|major|minor, description }.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/code-review-tools.ts",
    category: "codeReview",
    modes: "review"
  },
  {
    name: "get_dependencies",
    description: "Get this task's dependencies and their met/unmet status (met = merged to dev). Use to confirm blockers merged, or see why a task cannot start. For task state use get_task.",
    readOnly: true,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/dependency-suggestion-tools.ts",
    category: "global",
    modes: "all PM/agent modes (common tools)"
  },
  {
    name: "get_suggestions",
    description: "List project suggestions sorted by vote score. Filter by status or cap with limit (default 20). Suggestions are project-level ideas, not tasks \u2014 use get_task for tasks.",
    readOnly: true,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/dependency-suggestion-tools.ts",
    category: "global",
    modes: "all PM/agent modes (common tools)"
  },
  {
    name: "update_task_properties",
    description: "Set one or more task properties in a single call. All fields are optional \u2014 only include the fields you want to update.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/discovery-tools.ts",
    category: "discovery",
    modes: "discovery, auto"
  },
  {
    name: "add_dependency",
    description: "Add a blocking dependency \u2014 this task cannot start until the named task is merged to dev. For post-task follow-ups use create_follow_up_task instead.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/mutation-tools.ts",
    category: "global",
    modes: "all PM/agent modes (common tools)"
  },
  {
    name: "create_follow_up_task",
    description: "Create a follow-up task that depends on the current task. Use for out-of-scope work or cleanup that should land after this task merges. For blockers use add_dependency.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/mutation-tools.ts",
    category: "global",
    modes: "all PM/agent modes (common tools)"
  },
  {
    name: "create_pull_request",
    description: "Create a GitHub PR for this task. Auto-stages, commits (commitMessage or title default), pushes to origin, then opens the PR. Always use this instead of gh CLI or raw git.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/mutation-tools.ts",
    category: "global",
    modes: "all PM/agent modes (common tools)"
  },
  {
    name: "create_suggestion",
    description: "Suggest a feature, improvement, rule, or idea for the project. Duplicates are deduped and your upvote is recorded. For actionable work on this task open a follow-up task.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/mutation-tools.ts",
    category: "global",
    modes: "all PM/agent modes (common tools)"
  },
  {
    name: "force_update_task_status",
    description: "EMERGENCY ONLY: force-override a task's Kanban status. Use when an automatic transition failed and the task is wedged. Normal flow transitions status automatically.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/mutation-tools.ts",
    category: "global",
    modes: "all modes (emergency override; always exposed)"
  },
  {
    name: "post_to_chat",
    description: "Post a message to the task chat for the team to see. Your turn output is NOT shown in chat, so this is the only way the team sees your status, summaries, and questions. Omit task_id to post to the current task's chat; pass a child's ID to message its chat.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/mutation-tools.ts",
    category: "global",
    modes: "all PM/agent modes (common tools)"
  },
  {
    name: "remove_dependency",
    description: "Remove a previously added dependency from this task. When to use: the dependency was added in error or is no longer relevant. Returns: confirmation string.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/mutation-tools.ts",
    category: "global",
    modes: "all PM/agent modes (common tools)"
  },
  {
    name: "vote_suggestion",
    description: "Vote +1 or -1 on a project suggestion. Use to express support or disagreement with a specific suggestion returned by get_suggestions.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/mutation-tools.ts",
    category: "global",
    modes: "all PM/agent modes (common tools)"
  },
  {
    name: "approve_and_merge_pr",
    description: "Approve and merge a child task's PR. Preconditions: child in ReviewPR. Returns { merged }: true = merged (status\u2192ReviewDev); false = automerge queued, wait for ReviewDev.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/pm-tools.ts",
    category: "packRunner",
    modes: "parent task only (review, auto, discovery, help, building)"
  },
  {
    name: "create_subtask",
    description: "Create a subtask under the current parent task. Use when breaking a complex parent into smaller pieces during planning. For post-task follow-ups use create_follow_up_task.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/pm-tools.ts",
    category: "pm",
    modes: "review, auto, discovery, help; building (parent task only). Pack tools (start_child_cloud_build, stop_child_build, approve_and_merge_pr) require parent task."
  },
  {
    name: "delete_subtask",
    description: "Delete a subtask by id. When to use: a subtask was created in error or is no longer needed. Returns: confirmation string.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/pm-tools.ts",
    category: "pm",
    modes: "review, auto, discovery, help; building (parent task only). Pack tools (start_child_cloud_build, stop_child_build, approve_and_merge_pr) require parent task."
  },
  {
    name: "list_subtasks",
    description: "List all subtasks under the current parent task. Use to coordinate child work \u2014 check who is running, ready for review, or blocked. For non-child tasks use get_task.",
    readOnly: true,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/pm-tools.ts",
    category: "pm",
    modes: "review, auto, discovery, help; building (parent task only). Pack tools (start_child_cloud_build, stop_child_build, approve_and_merge_pr) require parent task."
  },
  {
    name: "start_child_cloud_build",
    description: "Start a cloud build (codespace) for a child task. Preconditions: child is in Open status, has a story point value, and has an agent assigned.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/pm-tools.ts",
    category: "packRunner",
    modes: "parent task only (review, auto, discovery, help, building)"
  },
  {
    name: "stop_child_build",
    description: "Send a graceful stop signal to a running child build's agent. Not a force-kill \u2014 the agent may take a moment to wind down.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/pm-tools.ts",
    category: "packRunner",
    modes: "parent task only (review, auto, discovery, help, building)"
  },
  {
    name: "update_subtask",
    description: "Update an existing subtask's fields (title, description, plan, ordinal, storyPointValue). Use when refining a child's plan or reordering. For the current task use update_task_plan.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/pm-tools.ts",
    category: "pm",
    modes: "review, auto, discovery, help; building (parent task only). Pack tools (start_child_cloud_build, stop_child_build, approve_and_merge_pr) require parent task."
  },
  {
    name: "update_task_plan",
    description: "Save the finalized plan and/or description to the current task. Use in Plan mode after alignment. For children use update_subtask; for title/tags/PR use update_task_properties.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/pm-tools.ts",
    category: "pm",
    modes: "task mode: discovery, auto only. PM/agent modes: review, auto, discovery, help; building (parent task only)."
  },
  {
    name: "get_attachment",
    description: "Fetch one task file's content plus metadata by file ID. Call list_task_files first to discover IDs and check sizes \u2014 large binaries may be truncated by the service's size limit.",
    readOnly: true,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/task-context-tools.ts",
    category: "global",
    modes: "all PM/agent modes (read-only common tools)"
  },
  {
    name: "get_current_plan",
    description: "Re-read the current task's plan. Use when the user updated the plan or asked you to re-read it \u2014 otherwise the plan is already in initial context. For task metadata use get_task.",
    readOnly: true,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/task-context-tools.ts",
    category: "global",
    modes: "all PM/agent modes (read-only common tools)"
  },
  {
    name: "get_execution_logs",
    description: "Read CLI execution logs \u2014 agent reasoning, tool calls, and setup/dev-server output. Filter via source='agent' or 'application'. For human chat use read_task_chat.",
    readOnly: true,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/task-context-tools.ts",
    category: "global",
    modes: "all PM/agent modes (read-only common tools)"
  },
  {
    name: "get_task",
    description: "Look up any task by slug or ID. Returns JSON with id, slug, title, description, plan, status, branch, githubPRNumber, githubPRUrl, storyPoints. For children use list_subtasks.",
    readOnly: true,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/task-context-tools.ts",
    category: "global",
    modes: "all PM/agent modes (read-only common tools)"
  },
  {
    name: "list_task_files",
    description: "List all files attached to this task with metadata. Use before fetching a specific file to see what is available and how large each is. For file contents use get_attachment.",
    readOnly: true,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/task-context-tools.ts",
    category: "global",
    modes: "all PM/agent modes (read-only common tools)"
  },
  {
    name: "read_task_chat",
    description: "Read recent human/user chat messages for a task. Omit task_id for the current task; pass a child ID for a child's chat. For agent logs use get_execution_logs.",
    readOnly: true,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/task-context-tools.ts",
    category: "global",
    modes: "all PM/agent modes (read-only common tools)"
  },
  {
    name: "get_attachment",
    description: "Fetch one task file's content plus metadata by file ID (accepts task id or slug). Pass projectId to target a specific project; otherwise the configured default project is used. Images are returned as viewable image blocks. Large text files (logs, JSON) are returned in pages \u2014 use `offset`/`maxBytes` to read more, or fetch `downloadUrl` for the whole file. Call list_task_files first to discover IDs and sizes.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/attachments.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "list_task_files",
    description: "List all files attached to a task with metadata (no contents \u2014 fast and small). Pass projectId to target a specific project; otherwise the configured default project is used. Use before fetching a specific file to see what is available and how large each is. For file contents use get_attachment.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/attachments.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "upload_attachment",
    description: "Upload a local file as a task attachment (any file type, up to 25MB). Pass projectId to target a specific project; otherwise the configured default project is used. The file appears under the task's Files. Pass `comment` to also post it to the task chat in the same step.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/attachments.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "create_release",
    description: "Create a release for the project \u2014 the same flow as the Release button in the web UI. Pass projectId to target a specific project; otherwise the configured default project is used. Creates a release task with a release/YYYY.MM.N branch and a PR from the dev branch to the default branch. Omit taskIds to release ALL cards currently in Review (Dev); pass a subset to cherry-pick \u2014 a cloud build agent then cherry-picks those changes and resolves conflicts. Fails if a release is already in progress or no cards are in Review (Dev).",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/builds.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "delete_task_environment",
    description: "Delete a task environment, including durable Claudespace state. Pass projectId to target a specific project; otherwise the configured default project is used.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/builds.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "get_build_status",
    description: "Check codespace and agent status for a task. Pass projectId to target a specific project; otherwise the configured default project is used.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/builds.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "resume_task",
    description: "Resume a sleeping task Claudespace. Pass projectId to target a specific project; otherwise the configured default project is used.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/builds.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "sleep_task",
    description: "Sleep a task Claudespace, stopping compute while preserving durable state. Pass projectId to target a specific project; otherwise the configured default project is used.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/builds.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "start_task",
    description: "Start a cloud build (codespace) for a task. Pass projectId to target a specific project; otherwise the configured default project is used.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/builds.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "stop_task",
    description: "Compatibility alias for the legacy stop path. stop_task now performs the same durable sleep behavior as sleep_task, preserving task Claudespace state while stopping compute. Pass projectId to target a specific project; otherwise the configured default project is used.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/builds.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "approve_manual_test",
    description: "Sign off on (approve) a manual test step on a task on behalf of your authenticated user. Pass projectId to target a specific project; otherwise the configured default project is used. Identify the test by its title (case-insensitive). Use after you have verified the step passes.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/checklists.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "edit_manual_test",
    description: "Rename an existing manual test step on a task. Pass projectId to target a specific project; otherwise the configured default project is used. Identify the test by its current title (case-insensitive) and pass the new title to replace it.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/checklists.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "list_manual_tests",
    description: "List the manual test checklist items for a task. Pass projectId to target a specific project; otherwise the configured default project is used. Use to see what manual verification steps have already been recorded.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/checklists.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "query_manual_tests",
    description: "Query manual tests across many tasks in a project, grouped by task. Filter by card status (e.g. ReviewDev, ReviewLive, Complete) and/or test status (open | approved | rejected). Use to answer questions like 'show all OPEN manual tests in ReviewDev' or 'show all REJECTED manual tests in ReviewDev/ReviewLive with the failing reason'. With no filters it defaults to the needs-attention view: open+rejected tests on ReviewDev/ReviewLive cards. Pass projectId to target a specific project; otherwise the configured default project is used.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/checklists.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "reject_manual_test",
    description: "Flag an issue with (reject) a manual test step on a task on behalf of your authenticated user, recording the reason. Pass projectId to target a specific project; otherwise the configured default project is used. Identify the test by its title (case-insensitive). Use when the step fails verification.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/checklists.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "remove_manual_test",
    description: "Remove an existing manual test step from a task's checklist. Pass projectId to target a specific project; otherwise the configured default project is used. Identify the test by its title (case-insensitive).",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/checklists.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "set_manual_tests",
    description: "Add manual test steps to a task's checklist. Pass projectId to target a specific project; otherwise the configured default project is used. Existing items with the same title are automatically skipped (deduplication). Use to record specific manual verification steps that reviewers should follow when testing the task's PR.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/checklists.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "add_dependency",
    description: "Add a blocking dependency \u2014 this task cannot start until the named task is merged to dev. Pass projectId to target a specific project; otherwise the configured default project is used.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/dependencies.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "get_dependencies",
    description: "Get a task's dependencies and their met/unmet status (met = merged to dev). Pass projectId to target a specific project; otherwise the configured default project is used. Use to confirm blockers merged, or see why a task cannot start. For task state use get_task.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/dependencies.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "remove_dependency",
    description: "Remove a previously added dependency from a task. Pass projectId to target a specific project; otherwise the configured default project is used. The task is no longer blocked by the named task. Returns: confirmation string.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/dependencies.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "get_project_summary",
    description: "Get overall project status: task counts by status, active builds, repo info. Pass projectId to target a specific project; otherwise the configured default project is used.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/project.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "list_project_members",
    description: "List project members with user ID, name, email, and access level \u2014 use to resolve a person's name or email to a user ID for task assignment or review. Pass projectId to target a specific project; otherwise the configured default project is used.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/project.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "list_projects",
    description: "List Conveyor projects available to this MCP token's user. Use a returned project id as projectId on other tools when no default project is configured or when targeting a different project.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/project.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "create_pull_request",
    description: "Open a GitHub pull request for a task's existing branch (the branch must already be pushed to origin). Pass projectId to target a specific project; otherwise the configured default project is used. Moves the task to ReviewPR. Returns the PR number and URL.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/pull-request.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "create_subtask",
    description: "Create a subtask under a parent task. Pass projectId to target a specific project; otherwise the configured default project is used. Subtasks break a larger task into independently buildable pieces.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/subtasks.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "delete_subtask",
    description: "Delete a subtask by ID. Pass projectId to target a specific project; otherwise the configured default project is used. This is permanent \u2014 use update_subtask to set status to Cancelled if you only want to close it.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/subtasks.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "list_subtasks",
    description: "List all subtasks of a parent task with their status and ordering. Pass projectId to target a specific project; otherwise the configured default project is used.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/subtasks.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "update_subtask",
    description: "Update a subtask's fields: title, description, plan, status, ordering, or story points. Pass projectId to target a specific project; otherwise the configured default project is used. Moving a subtask beyond Planning auto-fills missing story points and agent assignment \u2014 don't spend turns on them.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/subtasks.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "create_suggestion",
    description: "Suggest a feature, improvement, rule, or idea for the project. Pass projectId to target a specific project; otherwise the configured default project is used. Duplicates are deduped and your upvote is recorded.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/suggestions.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "add_reviewer",
    description: "Add a project member as a reviewer on a task. Pass projectId to target a specific project; otherwise the configured default project is used. Idempotent \u2014 adding an existing reviewer is a no-op.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/tasks.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "approve_and_merge_pr",
    description: "Approve and merge a child task's pull request. Pass projectId to target a specific project; otherwise the configured default project is used. Only succeeds if all CI/CD checks are passing. The child task must be in ReviewPR status with a PR.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/tasks.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "approve_task",
    description: "Move a task forward in the review flow (ReviewPR -> ReviewDev, or -> Complete). Pass projectId to target a specific project; otherwise the configured default project is used.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/tasks.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "create_task",
    description: "Create a new task with title, description, and optional plan. Pass projectId to target a specific project; otherwise the configured default project is used. Icon, story points, and agent assignment are auto-filled when a task is created in (or later moved to) a status beyond Planning \u2014 don't spend turns on them.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/tasks.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "get_card_by_slug",
    description: "Get full card details by the slug from a card URL (/cards/<slug>) instead of a task ID. Pass projectId to target a specific project; otherwise the configured default project is used.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/tasks.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "get_task",
    description: "Get full task details including plan, chat history, PR info, subtasks, and build status. Pass projectId to target a specific project; otherwise the configured default project is used.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/tasks.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "get_task_logs",
    description: "Read CLI execution logs from a task. Pass projectId to target a specific project; otherwise the configured default project is used. Returns agent reasoning, tool calls, setup output, and other execution events. For human chat use read_task_chat.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/tasks.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "get_task_sessions",
    description: "Read compute-session (pod) state for a task and any ephemeral code-review cards targeting it: pod identity (instanceName), liveness (agentRunnerStatus, lastHeartbeatAt), lifecycle (status, stoppedAt, deletionRequestedAt), and the parent's code-review claim state. Use to diagnose stalled/dead agents or code-review runs. Pass projectId to target a specific project; otherwise the configured default project is used.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/tasks.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "list_tags",
    description: "List all project tags with their names, IDs, and colors. Pass projectId to target a specific project; otherwise the configured default project is used.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/tasks.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "list_tasks",
    description: "List project tasks, optionally filtered by status or assignment (a specific assignee, or unassigned tasks). Pass projectId to target a specific project; otherwise the configured default project is used. Returns summaries \u2014 plan omitted, description truncated; use get_task for full details.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/tasks.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "post_to_chat",
    description: "Post a message to a task's chat. Pass projectId to target a specific project; otherwise the configured default project is used.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/tasks.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "read_task_chat",
    description: "Read messages from a task's chat. Pass projectId to target a specific project; otherwise the configured default project is used. For agent execution logs use get_task_logs.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/tasks.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "remove_reviewer",
    description: "Remove a reviewer from a task. Pass projectId to target a specific project; otherwise the configured default project is used. Idempotent \u2014 removing a non-reviewer is a no-op.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/tasks.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "request_changes",
    description: "Post feedback and send task back to InProgress for more work. Pass projectId to target a specific project; otherwise the configured default project is used.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/tasks.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "search_tasks",
    description: "Search cards by tag name, text query, status, type, and/or assignment. Defaults to type=task \u2014 pass typeFilters to include incidents/suggestions. Pass projectId to target a specific project; otherwise the configured default project is used. Use tag names like 'agent-runner', not IDs. Returns summaries \u2014 plan omitted, description truncated; use get_task for full details.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/tasks.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "update_task",
    description: "Update task fields: title, description, plan, status, or assignment. Pass projectId to target a specific project; otherwise the configured default project is used. Moving a task beyond Planning auto-fills any missing icon, story points, and agent assignment \u2014 don't spend turns on them.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/tasks.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "workspace_attach_info",
    description: "Return SSH/SFTP attach metadata for a running task Claudespace, plus hosted preview URLs and configured preview ports. Optionally installs an OpenSSH public key for this attach session.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/workspace.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "workspace_preview_urls",
    description: "Return the hosted preview URLs and preview ports for a running task Claudespace. This mirrors the web UI preview link metadata.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/workspace.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "workspace_start_tunnel",
    description: "Start a local loopback tunnel through the MCP server to a running task Claudespace port. Use port 2222 for SSH/SFTP, or one of previewPorts for app access.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/workspace.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "workspace_stop_tunnel",
    description: "Stop a local workspace tunnel previously opened by workspace_start_tunnel.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/workspace.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  }
];

// src/types/agent-tool-registry.ts
function getToolsByCategory(category) {
  return MCP_TOOL_REGISTRY.filter((tool) => tool.category === category);
}

// src/constants/timezone.ts
var PROJECT_TIMEZONE = "America/Los_Angeles";

// src/constants/agent-session-runner.ts
var CODESPACE_SESSION_ACTIVE_RUNNER_STATUSES = [
  "connected",
  "idle",
  "busy",
  "running",
  "fetching_context",
  "waiting_for_input",
  "setup",
  "connecting"
];
var TERMINAL_TASK_STATUSES = ["Complete", "Cancelled"];

// src/constants/agent-chat-history.ts
var TASK_CHAT_HISTORY_LIMIT = 20;
var PM_CHAT_HISTORY_LIMIT = 40;
var AGENT_CHAT_HISTORY_FETCH_LIMIT = Math.max(TASK_CHAT_HISTORY_LIMIT, PM_CHAT_HISTORY_LIMIT) + 10;

// src/room-helpers.ts
function serviceRoom(service, entityId) {
  return `${service}:${entityId}`;
}
function userRoom(userId) {
  return `user:${userId}`;
}
function notificationUserRoom(userId) {
  return `notificationService:user:${userId}`;
}
function cliRoom(taskId) {
  return `task:${taskId}:cli`;
}
function projectCliRoom(projectId, userId) {
  return `project:${projectId}:cli:${userId}`;
}
function projectEventsRoom(projectId) {
  return `project:${projectId}:events`;
}

// src/utils/stat-date.ts
function todayInProjectTZ() {
  return (/* @__PURE__ */ new Date()).toLocaleDateString("en-CA", { timeZone: PROJECT_TIMEZONE });
}
function formatDateInProjectTZ(date) {
  return date.toLocaleDateString("en-CA", { timeZone: PROJECT_TIMEZONE });
}
function resolveStatDateRange(statDate, fallbackDays) {
  const today = todayInProjectTZ();
  if (statDate) {
    if (statDate.mode === "month" && typeof statDate.month === "number") {
      const sinceDate3 = `${statDate.year}-${String(statDate.month).padStart(2, "0")}-01`;
      const lastDay = new Date(statDate.year, statDate.month, 0).getDate();
      const endStr2 = `${statDate.year}-${String(statDate.month).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
      return { sinceDate: sinceDate3, untilDate: endStr2 < today ? endStr2 : today };
    }
    const sinceDate2 = `${statDate.year}-01-01`;
    const endStr = `${statDate.year}-12-31`;
    return { sinceDate: sinceDate2, untilDate: endStr < today ? endStr : today };
  }
  const days = fallbackDays ?? 30;
  const d = /* @__PURE__ */ new Date();
  d.setDate(d.getDate() - days);
  const sinceDate = formatDateInProjectTZ(d);
  return { sinceDate, untilDate: today };
}
function generateDateRange(sinceStr, untilStr) {
  const dates = [];
  const [sy, sm, sd] = sinceStr.split("-").map(Number);
  const [uy, um, ud] = untilStr.split("-").map(Number);
  let current = Date.UTC(sy, sm - 1, sd);
  const end = Date.UTC(uy, um - 1, ud);
  while (current <= end) {
    const d = new Date(current);
    const yyyy = d.getUTCFullYear();
    const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
    const dd = String(d.getUTCDate()).padStart(2, "0");
    dates.push(`${yyyy}-${mm}-${dd}`);
    current += 24 * 60 * 60 * 1e3;
  }
  return dates;
}

// src/utils/message-filter.ts
function messageMatchesFilters(msg, channelFilters) {
  if (!channelFilters || channelFilters.length === 0) return true;
  if (msg.role === "user") return true;
  const filterSet = new Set(channelFilters);
  const isToolCall = msg.metadata?.type === "activity_block";
  if (isToolCall) return filterSet.has("tools");
  return filterSet.has(msg.channel ?? "agent");
}

// src/utils/mentions.ts
var MENTION_TOKEN_REGEX = /@\[(\w+):([^\]]+)\]/g;
function serializeMention(type, id) {
  return `@[${type}:${id}]`;
}
function buildMentionKey(type, id) {
  return `${type}:${id}`;
}
function parseMentions(content) {
  const tokens = [];
  for (const match of content.matchAll(MENTION_TOKEN_REGEX)) {
    tokens.push({ type: match[1], id: match[2] });
  }
  return tokens;
}
function resolveMentions(content, metadata) {
  if (!metadata || typeof metadata !== "object") return content;
  const meta = metadata;
  if (meta.type !== "mentions" || !meta.mentions) return content;
  const mentions = meta.mentions;
  return content.replace(MENTION_TOKEN_REGEX, (_match, type, id) => {
    const key = `${type}:${id}`;
    const mention = mentions[key];
    if (!mention) return _match;
    const prefix = mention.slug ? `#${mention.slug}` : `@${mention.label}`;
    return `${prefix} (${type}:${id})`;
  });
}

// src/utils/autopilot-ratio.ts
var AUTOPILOT_RATIO_MIN = 1;
var AUTOPILOT_RATIO_MAX = 6.5;
function computeMemoryBounds(agentCpu, sidecarCpu, sidecarMemoryGi) {
  const totalCpu = agentCpu + sidecarCpu;
  const minPodMemory = totalCpu * AUTOPILOT_RATIO_MIN;
  const maxPodMemory = totalCpu * AUTOPILOT_RATIO_MAX;
  const minAgentMemory = minPodMemory - sidecarMemoryGi;
  const maxAgentMemory = maxPodMemory - sidecarMemoryGi;
  const minMemoryGi = Math.max(1, Math.ceil(minAgentMemory));
  const maxMemoryGi = Math.floor(maxAgentMemory);
  return { minMemoryGi, maxMemoryGi };
}
function computePodRatio(agentCpu, agentMemoryGi, sidecarCpu, sidecarMemoryGi) {
  const totalCpu = agentCpu + sidecarCpu;
  const totalMemory = agentMemoryGi + sidecarMemoryGi;
  if (totalCpu === 0) return 0;
  return totalMemory / totalCpu;
}
function isValidAutopilotConfig(agentCpu, agentMemoryGi, sidecarCpu, sidecarMemoryGi) {
  const ratio = computePodRatio(agentCpu, agentMemoryGi, sidecarCpu, sidecarMemoryGi);
  return ratio >= AUTOPILOT_RATIO_MIN && ratio <= AUTOPILOT_RATIO_MAX;
}

// src/utils/sidecar-overhead.ts
var SIDECAR_OVERHEAD = {
  postgresql: {
    // 50m
    cpu: 0.05,
    // 128Mi
    memoryGi: 0.125
  },
  redis: {
    // 25m
    cpu: 0.025,
    // 32Mi
    memoryGi: 0.03125
  },
  elasticsearch: {
    // 100m
    cpu: 0.1,
    // 256Mi
    memoryGi: 0.25
  },
  lgtm: {
    // 250m — bundles Grafana + Loki + Tempo + Mimir + OTEL Collector
    cpu: 0.25,
    // 1Gi
    memoryGi: 1
  },
  "firebase-auth-emulator": {
    // 100m
    cpu: 0.1,
    // 256Mi
    memoryGi: 0.25
  }
};
function sidecarRequestTotals(deps) {
  let totalCpu = 0;
  let totalMemoryGi = 0;
  for (const dep of deps) {
    const overhead = SIDECAR_OVERHEAD[dep];
    if (overhead) {
      totalCpu += overhead.cpu;
      totalMemoryGi += overhead.memoryGi;
    }
  }
  return { cpu: totalCpu, memoryGi: totalMemoryGi };
}

// src/utils/xp.ts
var SP_XP_RATE = 10;
var REPORT_XP_RATE = 10;
var REVIEW_XP_RATE = 5;
var MAX_LEVEL = 99;
function xpToReachLevel(level) {
  if (level <= 1) return 0;
  return 5 * level * (level - 1);
}
function computeTotalXp({ spValueSum, reportCount, reviewCount }) {
  const spXp = spValueSum * SP_XP_RATE;
  const reportXp = reportCount * REPORT_XP_RATE;
  const reviewXp = reviewCount * REVIEW_XP_RATE;
  return { spXp, reportXp, reviewXp, totalXp: spXp + reportXp + reviewXp };
}
function levelFromXp(totalXp) {
  const xp = Number.isFinite(totalXp) && totalXp > 0 ? Math.floor(totalXp) : 0;
  let level = Math.floor((5 + Math.sqrt(25 + 20 * xp)) / 10);
  while (xpToReachLevel(level + 1) <= xp) level++;
  while (level > 1 && xpToReachLevel(level) > xp) level--;
  level = Math.min(MAX_LEVEL, Math.max(1, level));
  if (level >= MAX_LEVEL) {
    return {
      level: MAX_LEVEL,
      xpIntoLevel: xp - xpToReachLevel(MAX_LEVEL),
      xpForNextLevel: 0,
      progress: 1
    };
  }
  const floor = xpToReachLevel(level);
  const xpForNextLevel = xpToReachLevel(level + 1) - floor;
  const xpIntoLevel = xp - floor;
  const progress = xpForNextLevel === 0 ? 1 : Math.min(1, Math.max(0, xpIntoLevel / xpForNextLevel));
  return { level, xpIntoLevel, xpForNextLevel, progress };
}
export {
  AGENT_CHAT_HISTORY_FETCH_LIMIT,
  ALLOWED_FILE_MIME_TYPES,
  AUTOPILOT_RATIO_MAX,
  AUTOPILOT_RATIO_MIN,
  AddDependencyRequestSchema,
  AddProjectTaskDependencyRequestSchema,
  AddProjectTaskReviewerRequestSchema,
  AgentEventSchema,
  AgentHeartbeatSchema,
  AgentQuestionOptionSchema,
  AgentQuestionSchema,
  AnswerAgentQuestionRequestSchema,
  ApproveAndMergePRRequestSchema,
  ApproveManualTestRequestSchema,
  ApproveProjectManualTestRequestSchema,
  ApproveProjectMergePRRequestSchema,
  AskUserQuestionRequestSchema,
  CARD_TYPE_STATUSES,
  CARD_TYPE_VALUES,
  CODESPACE_SESSION_ACTIVE_RUNNER_STATUSES,
  CRITICAL_AUTOMATED_SOURCES,
  CancelTaskQueuedMessageRequestSchema,
  ClearAgentTodosRequestSchema,
  ConfirmFileUploadRequestSchema,
  ConfirmProjectFileUploadRequestSchema,
  ConnectAgentRequestSchema,
  CreateFollowUpTaskRequestSchema,
  CreatePRInputSchema,
  CreatePRResponseSchema,
  CreateProjectPullRequestRequestSchema,
  CreateProjectReleaseRequestSchema,
  CreateProjectSubtaskRequestSchema,
  CreateProjectSuggestionRequestSchema,
  CreateProjectTaskRequestSchema,
  CreatePullRequestRequestSchema,
  CreateSubtaskRequestSchema,
  CreateSuggestionRequestSchema,
  DEFAULT_HAIKU_MODEL,
  DEFAULT_OPUS_MODEL,
  DEFAULT_SONNET_MODEL,
  DEPLOYMENT_TARGET_ENVIRONMENTS,
  DEPLOYMENT_TARGET_PROVIDERS,
  DeleteProjectSubtaskRequestSchema,
  DeleteSubtaskRequestSchema,
  DeleteSubtaskResponseSchema,
  EMBEDDABLE_IMAGE_TYPES,
  EditManualTestRequestSchema,
  EditProjectManualTestRequestSchema,
  EmitAgentEventRequestSchema,
  FlushSingleQueuedMessageRequestSchema,
  FlushTaskQueueRequestSchema,
  GCP_ENVS,
  GenerateTaskIconRequestSchema,
  GetActivePtySessionRequestSchema,
  GetAgentStatusRequestSchema,
  GetChatMessagesRequestSchema,
  GetCliHistoryRequestSchema,
  GetCumulativeSpendingRequestSchema,
  GetCumulativeSpendingResponseSchema,
  GetDependenciesRequestSchema,
  GetProjectAttachmentRequestSchema,
  GetProjectSummaryRequestSchema,
  GetProjectTaskChatRequestSchema,
  GetProjectTaskCliRequestSchema,
  GetProjectTaskDependenciesRequestSchema,
  GetProjectTaskRequestSchema,
  GetProjectTaskSessionsRequestSchema,
  GetSuggestionsRequestSchema,
  GetTaskContextRequestSchema,
  GetTaskFileRequestSchema,
  GetTaskFilesRequestSchema,
  GetTaskPropertiesRequestSchema,
  GetTaskRequestSchema,
  GetUiCliHistoryRequestSchema,
  HeartbeatRequestSchema,
  HeartbeatResponseSchema,
  IDLE_HEARTBEAT_MS,
  ListAccessibleProjectsRequestSchema,
  ListIconsRequestSchema,
  ListManualTestsRequestSchema,
  ListProjectManualTestsRequestSchema,
  ListProjectMembersRequestSchema,
  ListProjectSubtasksRequestSchema,
  ListProjectTagsRequestSchema,
  ListProjectTaskFilesRequestSchema,
  ListProjectTasksRequestSchema,
  ListSubtasksRequestSchema,
  MAX_FILE_SIZE_BYTES,
  MAX_LEVEL,
  MCP_TOOL_REGISTRY,
  MENTION_TOKEN_REGEX,
  ModelUsageEntrySchema,
  NotifyAgentVersionRequestSchema,
  PM_CHAT_HISTORY_LIMIT,
  PREVIEW_PORT_DENY_LIST,
  PREVIOUS_SONNET_MODEL,
  PROJECT_FUNCTION_DEFAULTS,
  PROJECT_TIMEZONE,
  PickFaIconRequestSchema,
  PostChildChatMessageRequestSchema,
  PostToChatInputSchema,
  PostToChatRequestSchema,
  PostToChatResponseSchema,
  PostToProjectTaskChatRequestSchema,
  PtyAttachRequestSchema,
  PtyInputRequestSchema,
  PtyOutputRequestSchema,
  PtyResizeRequestSchema,
  QueryManualTestsRequestSchema,
  QueryProjectManualTestsRequestSchema,
  REPORT_XP_RATE,
  RETIRED_SONNET_MODEL,
  REVIEW_XP_RATE,
  RefreshGithubTokenRequestSchema,
  RefreshGithubTokenResponseSchema,
  RejectManualTestRequestSchema,
  RejectProjectManualTestRequestSchema,
  RemoveDependencyRequestSchema,
  RemoveManualTestRequestSchema,
  RemoveProjectManualTestRequestSchema,
  RemoveProjectTaskDependencyRequestSchema,
  RemoveProjectTaskReviewerRequestSchema,
  ReportAgentStatusRequestSchema,
  RequestFileUploadRequestSchema,
  RequestProjectFileUploadRequestSchema,
  SIDECAR_OVERHEAD,
  SP_XP_RATE,
  STRANDED_RUNNER_STATUSES,
  SearchFaIconsRequestSchema,
  SearchProjectTasksRequestSchema,
  SendSoftStopRequestSchema,
  SessionStartRequestSchema,
  SessionStartResponseSchema,
  SessionStopRequestSchema,
  SessionStopResponseSchema,
  SetManualTestsRequestSchema,
  SetProjectManualTestsRequestSchema,
  StartChildCloudBuildRequestSchema,
  StartProjectBuildRequestSchema,
  StopChildBuildRequestSchema,
  StopProjectBuildRequestSchema,
  StoreSessionIdRequestSchema,
  StoreSessionIdResponseSchema,
  SubmitCodeReviewResultRequestSchema,
  TASK_CHAT_HISTORY_LIMIT,
  TASK_STATUS_ORDER,
  TERMINAL_TASK_STATUSES,
  TrackSpendingRequestSchema,
  TriggerIdentificationRequestSchema,
  UpdateChildStatusRequestSchema,
  UpdateProjectSubtaskRequestSchema,
  UpdateProjectTaskRequestSchema,
  UpdateSubtaskRequestSchema,
  UpdateTaskFieldsRequestSchema,
  UpdateTaskPropertiesRequestSchema,
  UpdateTaskStatusRequestSchema,
  UpdateTaskStatusResponseSchema,
  VoteProjectSuggestionRequestSchema,
  VoteSuggestionRequestSchema,
  buildMentionKey,
  cliRoom,
  computeMemoryBounds,
  computePodRatio,
  computeTotalXp,
  createAgentSessionRoom,
  createProjectRoom,
  deriveConnectionState,
  formatHeartbeatAge,
  generateDateRange,
  getToolsByCategory,
  isAllowablePreviewPort,
  isCuid,
  isEmbeddableFile,
  isValidAutopilotConfig,
  levelFromXp,
  messageMatchesFilters,
  notificationUserRoom,
  parseMentions,
  pickPreferredPreviewPort,
  projectCliRoom,
  projectEventsRoom,
  resolveAllowedPreviewPorts,
  resolveCodeReviewMode,
  resolveGcpEnvResources,
  resolveMentions,
  resolveProjectFunctionConfig,
  resolveStatDateRange,
  sanitizeSessionPreviewPorts,
  serializeMention,
  serviceRoom,
  sidecarRequestTotals,
  todayInProjectTZ,
  userRoom,
  validateStatusForType,
  xpToReachLevel
};
